import { useState } from 'react';
import { tokens } from '../tokens';
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect';
function mediaQueryStringFromBreakpoint(breakpoint) {
    return `screen and (min-width: ${breakpoint})`;
}
const breakpointsToResultsMap = {
    isAboveLarge: mediaQueryStringFromBreakpoint(tokens.breakpoints.large),
    isAboveMedium: mediaQueryStringFromBreakpoint(tokens.breakpoints.medium),
    isAboveSmall: mediaQueryStringFromBreakpoint(tokens.breakpoints.small),
};
function initialMatchResults() {
    const keys = Object.keys(breakpointsToResultsMap);
    return keys.reduce((results, resultKey) => {
        return Object.assign(Object.assign({}, results), { [resultKey]: false });
    }, {});
}
/**
 * For each breakpoint from design tokens (small, medium, large), determine if there is a media query match between it and a current viewport.
 *
 * Following mobile-first approach, when using the hook, consider base return without conditional rendering to be the mobile one.
 *
 * @returns Object of boolean values with `isAboveSmall`, `isAboveMedium`, and `isAboveLarge` entries
 */
function useMediaQuery() {
    const [matchResults, setMatchResults] = useState(initialMatchResults());
    useIsomorphicLayoutEffect(() => {
        function handleMediaChange() {
            const results = Object.keys(breakpointsToResultsMap).reduce((prevResults, resultKey) => {
                return Object.assign(Object.assign({}, prevResults), { [resultKey]: window.matchMedia(breakpointsToResultsMap[resultKey]).matches });
            }, {});
            setMatchResults(results);
        }
        handleMediaChange();
        // Add event listeners for each breakpoint
        // Safari doesn't support addEventListener for matchMedia, so using addListener as a fallback
        Object.values(breakpointsToResultsMap).forEach((queryString) => {
            const media = window.matchMedia(queryString);
            try {
                media.addEventListener('change', handleMediaChange);
            }
            catch (err) {
                media.addListener(handleMediaChange);
            }
        });
        return () => {
            Object.values(breakpointsToResultsMap).forEach((queryString) => {
                const media = window.matchMedia(queryString);
                try {
                    media.removeEventListener('change', handleMediaChange);
                }
                catch (err) {
                    media.removeListener(handleMediaChange);
                }
            });
        };
    }, []);
    return matchResults;
}
export default useMediaQuery;
